import { useContext, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import PropTypes from 'prop-types';
import { REGISTRATION_CUSTOMER_TYPE } from 'components/common/constants';
import i18nContext from 'components/i18n-context';
import { trimTextContent } from 'services/utils';
import { ROUTE_PATHS } from 'routes/constants';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import './CustomerRegistrationTypeChoosing.scss';

const CustomerRegistrationTypeChoosing = ({
  registrationStore,
  isUserLoggedIn,
  hasCustomerAccounts,
  hasIndividualCustomer,
  shouldShowInviteCode
}) => {
  const i18n = useContext(i18nContext);
  const navigate = useNavigate();
  const personalCustomerRegistrationInvitationCode =
    process.env.REACT_APP_PERSONAL_CUSTOMER_REGISTRATION_INVITATION_CODE;
  const [inviteCodeError, setInvitationCodeError] = useState('');

  const handleChangeInvitationCode = (e) => {
    const inputValue = trimTextContent(e?.target?.value);
    registrationStore.setInviteCode(inputValue);
    setInvitationCodeError('');
  };

  const handleBlurInvitationCode = () => {
    if (registrationStore.inviteCode !== personalCustomerRegistrationInvitationCode) {
      setInvitationCodeError(i18n.getMessage('register.error.invalidInviteCode'));
    } else {
      setInvitationCodeError('');
    }
  };

  // Determine if personal registration button should be disabled
  const isPersonalButtonDisabled = useMemo(() => {
    // If logged in with customers but no individual - allow without invite code
    if (isUserLoggedIn && hasCustomerAccounts && !hasIndividualCustomer) {
      return false;
    }

    // For all other cases (including user has individual customer) - disable button
    if (hasIndividualCustomer) {
      return true;
    }

    // Default case - check invite code
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserLoggedIn, hasCustomerAccounts, hasIndividualCustomer, inviteCodeError, registrationStore.inviteCode]);

  const handleClickToPersonalRegistrationButton = () => {
    if (shouldShowInviteCode) {
      if (!registrationStore.inviteCode) {
        setInvitationCodeError(i18n.getMessage('register.error.inviteCodeIsRequired'));
      } else if (registrationStore.inviteCode !== personalCustomerRegistrationInvitationCode) {
        setInvitationCodeError(i18n.getMessage('register.error.invalidInviteCode'));
      } else {
        setInvitationCodeError('');
        registrationStore.setRegistrationType(REGISTRATION_CUSTOMER_TYPE.PERSONAL);
        navigate(ROUTE_PATHS.PERSONAL_REGISTRATION);
      }
    } else {
      registrationStore.setRegistrationType(REGISTRATION_CUSTOMER_TYPE.PERSONAL);
      navigate(ROUTE_PATHS.PERSONAL_REGISTRATION);
    }
  };

  return (
    <div className={'customer-registration-type-choosing-wrapper'} id={'customer-registration-type-choosing'}>
      <div className={'customer-registration-type-choosing-card'} id={'customer-registration-type-choosing'}>
        <span className={'customer-registration-type-choosing-title'}>
          {i18n.getMessage('register.customerRegistrationTypeChoosing.title.company')}
        </span>
        <span className={'customer-registration-type-choosing-description'}>
          {i18n.getMessage('register.customerRegistrationTypeChoosing.description.company')}
        </span>
        <Button
          className={'customer-registration-type-choosing-button'}
          type={'primary'}
          size={'large'}
          onClick={() => {
            registrationStore.setRegistrationType(REGISTRATION_CUSTOMER_TYPE.BUSINESS);
            navigate(ROUTE_PATHS.COMPANY_REGISTRATION);
          }}
        >
          {i18n.getMessage('register.customerRegistrationTypeChoosing.button.next')}
        </Button>
      </div>

      <div className={'customer-registration-type-choosing-card'} id={'customer-registration-type-choosing'}>
        <span className={'customer-registration-type-choosing-title'}>
          {i18n.getMessage('register.customerRegistrationTypeChoosing.title.personal')}
        </span>
        <div className={'customer-registration-type-choosing-personal-wrapper'}>
          <span className={'customer-registration-type-choosing-description'}>
            {i18n.getMessage('register.customerRegistrationTypeChoosing.description.personal')}
          </span>
          {shouldShowInviteCode && (
            <Input
              className={'customer-registration-type-choosing-input'}
              autoComplete={'off'}
              label={i18n.getMessage('register.customerRegistrationTypeChoosing.inviteCodeInput.label')}
              name={'inviteCode'}
              value={registrationStore.inviteCode}
              error={inviteCodeError}
              onChange={handleChangeInvitationCode}
              onBlur={handleBlurInvitationCode}
              isDisabled={isPersonalButtonDisabled}
            />
          )}
        </div>
        <Button
          className={'customer-registration-type-choosing-button'}
          type={'primary'}
          size={'large'}
          isDisabled={isPersonalButtonDisabled}
          onClick={handleClickToPersonalRegistrationButton}
        >
          {i18n.getMessage('register.customerRegistrationTypeChoosing.button.next')}
        </Button>
      </div>
    </div>
  );
};

CustomerRegistrationTypeChoosing.propTypes = {
  registrationStore: MobXPropTypes.observableObject,
  isUserLoggedIn: PropTypes.bool,
  hasCustomerAccounts: PropTypes.bool,
  hasIndividualCustomer: PropTypes.bool,
  shouldShowInviteCode: PropTypes.bool
};

export default inject('registrationStore')(observer(CustomerRegistrationTypeChoosing));
